//@flow
import React, {useCallback, useState} from 'react';
import type {
  actTemplateId,
  agentId,
  contractId,
  document,
  organizationId
} from "../../../../../services/flowTypes/dataFlowTypes";
import {baseClass} from "../../../../../pages/UiKit/newUiKit/utilities/baseClass";
import type {addClasses} from "../../../../../services/flowTypes/uiKitFlowTypes";
import './Document.css';
import formatDate from "../../../../../utils/formatDate";
import formatTime from "../../../../../utils/formatTime";
import text from "../../../../../services/localization/text";
import type {
  descriptionEdit,
  documentBtns,
  isDocumentDescriptionEdit,
  isLinkAppearance,
  isUpload,
  itemType,
  linkIdToDownload
} from "../../../../../services/flowTypes/componentFlowTypes";
import RenderOnReady from "../../../../service/RenderOnReady/RenderOnReady";
import getDocumentType from "../../../../../utils/getDocumentType";
import getDocumentSize from "../../../../../utils/getDocumentSize";
import isPreviewSupported from "../../../../../utils/isPreviewSupported";
import TextOverflowHandler from "../../../../simple/Containers/TextOverflowHandler/TextOverflowHandler";
import {connect} from "react-redux";
import DocumentIndexStatusIcon from "../../../../simple/Icons/DocumentIndexStatusIcon/DocumentIndexStatusIcon";
import Text4 from "../../../../simple/TextComponents/Text4/Text4";
import HoveredIcon from "../../../HoveredIcon/HoveredIcon";
import fileIconSrc from "../../../../../assets/file-icon.svg";
import fileHoveredIconSrc from "../../../../../assets/file-hovered-icon.svg";
import {Link} from "react-router-dom";
import DocumentPreview from "../DocumentPreview/DocumentPreview";
import api from "../../../../../services/apiInstance/api";
import createTempLinkToDownloadFile from "../../../../../utils/createTempLinkToDownloadFile";
import apiErrorHandler from "../../../../../services/apiInstance/apiErrorHandler";
import SmallTextBtn from "../../../../simple/Buttons/SmallTextBtn/SmallTextBtn";
import {DownloadIcon, EyeIcon} from "lucide-react";
import Tooltip from "../../../../../pages/UiKit/newUiKit/Tooltip/Tooltip";
import preventEventPropagation from "../../../../../utils/preventEventPropagation";
import TagLabel from "../../../../simple/TextComponents/TagLabel/TagLabel";

type Props = {
  document: document,
  addClasses?: addClasses,
  itemType?: itemType,
  itemId: contractId | agentId | actTemplateId,
  documentBtns?: documentBtns,
  isDocumentDescriptionEdit?: isDocumentDescriptionEdit,
  descriptionAndFolderEdit?: descriptionEdit,
  isUpload?: isUpload,
  actionName?: string,
  showTime?: boolean,
  linkIdToDownload?: linkIdToDownload,
  isDocumentEdit?: boolean,
  isLinkAppearance?: isLinkAppearance,
  organizationId: organizationId,
  pathname?: string,
  isGlobalTemplate?: boolean,
};

const DocumentFunctional = (props: Props) => {
  const {
    document,
    addClasses,
    itemType,
    itemId,
    documentBtns,
    isDocumentDescriptionEdit,
    descriptionAndFolderEdit,
    actionName = 'downloadDocument',
    showTime = true,
    linkIdToDownload,
    isDocumentEdit,
    isLinkAppearance,
    organizationId,
    pathname,
    isGlobalTemplate
  } = props;

  const [isReadyToRender, setIsReadyToRender] = useState<boolean>(true);
  const [errorObject, setErrorObject] = useState<?errorObject>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

  const openPreview = useCallback((e) => {
    preventEventPropagation(e)
    setIsPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  const checkPreviewSupported = useCallback(() => {
    return isPreviewSupported(document.name);
  }, [document.name]);

  const folderUrl = document.folderId && pathname ?
    pathname.includes('/folder/') ? pathname : pathname + "/folder/" + document.folderId
    : '';

  const urlencodedDocumentName = encodeURIComponent(document.name);

  const filePart = document.fileId !== undefined ? `/${document.fileId}/${urlencodedDocumentName}` : '';
  const linkToDownloadDoc: string = `/${itemType}/${itemId}/${actionName}${filePart}?o_id=${organizationId}`;

  if (isLinkAppearance) {
    return (
      <a className={'BtnEditAct'} target={"_blank"} rel="noopener noreferrer"
         href={linkToDownloadDoc}>
        <TextOverflowHandler addClasses={'TextOverflowHandler_mw-100'}>
          {document.name}
        </TextOverflowHandler>
      </a>
    );
  }

  const previewSupported = checkPreviewSupported();
  return (
    <>
      {isDocumentEdit ? props.editModal : null}
      <div className={baseClass("Document", addClasses)}>
        <RenderOnReady isReadyToRender={isReadyToRender} errorObject={errorObject}/>

        <div className="Document__left">
          <div className="Document__icon-container">
            <div
              className={"mb_1du doc__ico"}
              style={{
                position: 'relative',
                cursor: previewSupported ? 'pointer' : 'default'
              }}
              onClick={previewSupported ? openPreview : undefined}
            >
              <Tooltip >Code is copied</Tooltip>
              <HoveredIcon baseIconSrc={fileIconSrc} hoveredIconSrc={fileHoveredIconSrc}/>
              <div className="doc__ico-content">{getDocumentType(document.name)}</div>
              {previewSupported && (
                <div className="doc_eye">
                  <EyeIcon size={22} />
                </div>
              )}
            </div>
          </div>
          <div className="Document__name-and-data">
            <div className="Document__name-container flex">
              <SmallTextBtn
                id={linkIdToDownload}
                preIcons={<DownloadIcon size={14}/>}
                addClasses={"SmallTextBtn--link SmallTextBtn--large"}
                className="Document__name-container flex flex_jc-sb"
                onClick={(e) => {
                  preventEventPropagation(e)
                  // Проверяем, есть ли уже загруженный файл в document.file
                  if (document.file) {
                    // Если файл уже загружен (File объект из input), создаем ссылку для скачивания напрямую
                    createTempLinkToDownloadFile(document.file, document.name);
                    return;
                  }

                  // Если файл не загружен, загружаем через API
                  setIsReadyToRender(false);
                  api.download(linkToDownloadDoc)
                    .then(response => {
                      createTempLinkToDownloadFile(response.data, document.name)
                    })
                    .catch((error) => {
                      setErrorObject(apiErrorHandler(error));
                    })
                    .finally(() => {
                      setIsReadyToRender(true);
                    });
                }}>
                <TextOverflowHandler addClasses={'TextOverflowHandler_mw-100'}>
                  {document.name}
                </TextOverflowHandler>
              </SmallTextBtn>
              {isGlobalTemplate && (
                <TagLabel addClasses={'ml_2du'}>Из списка</TagLabel>
              )}
            </div>
            <div className="Document__description-container">
              {isDocumentDescriptionEdit ? (
                descriptionAndFolderEdit
              ) : (
                <Text4>{document.fileDescription}</Text4>
              )}
            </div>
            <div className="Document__size-and-index">
              {document.hasOwnProperty('size') ?
                <Text4 addClasses='doc__size Text4_fs10'>{getDocumentSize(document.size)}</Text4> : null}
              {document.indexStatus ? (
                <DocumentIndexStatusIcon indexStatus={document.indexStatus} addClasses={'mr_2du'}
                                         indexStatusMessage={document.indexStatusMessage}/>
              ) : null}
              {document.folderId && pathname ? (
                <div className="Document__linkToFolder">
                  <div className="Document__linkToFolderIco"/>
                  <Link className={'Text4 Text4_linkColorOnHover'} to={folderUrl}>
                    <TextOverflowHandler
                      addClasses={'TextOverflowHandler_mw-100'}>{document.folderName}</TextOverflowHandler>
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {(documentBtns || document.createDate) && <div className="Document__right">
          {document.createDate && (
            <Text4 addClasses={'Document__date'}>
              {showTime
                ? formatDate(document.createDate) + ' ' + text.prepositionAtTime + ' ' + formatTime(document.createDate)
                : formatDate(document.createDate)
              }
            </Text4>
          )}
          {documentBtns}
        </div>}
        {document.isFileDeleted ? (
          <div className="Document__isDeletedMessage">{text.fileWillBeDeleted}</div>
        ) : null}
      </div>

      <DocumentPreview
        downloadLink={linkToDownloadDoc}
        document={document}
        itemType={itemType}
        organizationId={organizationId}
        isOpen={isPreviewOpen}
        onClose={closePreview}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    organizationId: ownProps.organizationId || state.organization.organizationId
  };
};

export default connect(mapStateToProps, null)(DocumentFunctional);
